import _ from "lodash";
import client from "lib/apolloClient/client";
import { graphql } from "lib/apolloClient";
import { getOrganizationGradeBasicDetailsQuery } from "modules/CommonQuery";
import { getOrganizationBasicGradeDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  getAssessmentQuestionsQuery,
  getStudentAssignmentQuestionSubmissionsQuery,
  getQuestionTopicsQuery,
  getStudentQuestionDetailsQuery,
  getStudentAssignmentDetailsQueryV2,
  getStudentAssignmentQueryV2,
  getOrganizationSubjectsQuery,
  getAssessmentQuestionsQueryV2,
  getStudentQuestionDetailsQueryV2,
  getWorksheetDownloadJobCountQuery,
  getAIConversationsQuery,
  getToddleQuestionLibraryFeedQueryV2,
} from "./WorksheetQueries";
import {
  getAssessmentQuestionsFromCache,
  getStudentAssignmentQuestionSubmissionsFromCache,
  getQuestionTopicsFromCache,
  getOrganizationSubjectsFromCache,
} from "./WorksheetGraphqlHelpers";
import { TODDLE_LIBRARY, SCHOOL_LIBRARY } from "./WorksheetConstants";
import {
  getWorksheetSectionsData,
  getWorksheetSubmissions,
  generateQuestionsHierarchy,
  getSideBarOptions,
  getSideBarOptionsV2,
} from "./WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "./WorksheetFeatureFlags";
import { prepareLoadMoreFeed } from "Utils";
import { CACHE_AND_NETWORK } from "Constants/stringConstants";
import { getToddleLibraryWorksheetDetails } from "Login/routes/ToddleLibrary/modules/ToddleLibraryUtils";
import { refetchGetStudentAssignmentDetails } from "./WorksheetModules";
import { getIsStudentAssignmentRevampEnabled } from "ClassRoom/modules/utils";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { Sentry } from "services/sentry";

const EMPTY_OBJECT = {};

/**
 * @deprecated use getAssessmentQuestionsEnhancerV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getAssessmentQuestionsEnhancer = () => {
  return graphql(getAssessmentQuestionsQuery, {
    name: "getAssessmentQuestions",
    skip: ({
      parentId,
      parentType,
      isFromToddleLibrary,
      userType,
      importFromToddleLibrary,
    }) =>
      _.isNil(parentId) ||
      _.isNil(parentType) ||
      !_.isEqual(parentType, "ASSESSMENT") ||
      isWorksheetQueriesV2Enabled(importFromToddleLibrary) ||
      _.includes(["student", "parent"], userType) ||
      isFromToddleLibrary,
    options: ({ assessmentQuestionsQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentQuestionsQueryVariables,
      context: { useHttpLink: true },
    }),
    props({
      getAssessmentQuestions,
      ownProps: {
        isData,
        isLoading,
        fetchToddleLibraryWorksheetDetails = false,
        updateExtractFromDoc,
        updateActiveQuestion,
        updateWorksheetSidebar,
        parentId,
        portalType,
        collaboratorsFilter,
        setToastMsg,
        isToddleLibraryPortal,
      },
    }) {
      const { variables, networkStatus } = getAssessmentQuestions;

      const cacheData = getAssessmentQuestionsFromCache(variables);

      const toddleLibraryWorksheetDetails = fetchToddleLibraryWorksheetDetails
        ? getToddleLibraryWorksheetDetails(cacheData)
        : EMPTY_OBJECT;
      const data = getWorksheetSectionsData(cacheData);

      const parentResourceId = _.get(
        cacheData,
        "organizationResourcesV2.edges[0].node.id",
        null
      );
      const parentItemId = _.get(
        cacheData,
        "organizationResourcesV2.edges[0].node.item.id",
        null
      );

      return {
        data,
        toddleLibraryWorksheetDetails,
        parentResourceId,
        parentItemId,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        getAssessmentQuestions,
        onPusherEventTriggered: async params => {
          switch (params?.event) {
            case "questionExtractionJob": {
              const assessmentId = _.get(params, "assessment_id");
              const taskCompleted = _.get(params, "taskState") === "COMPLETED";
              const taskFailed = _.get(params, "taskState") === "FAILED";
              const percentage = _.get(params, "percentage", 0);
              const extractionConversationId = _.get(
                params,
                "aiConversationId"
              );
              // In toddle library portal, parentId is the resource container ID;
              // parentItemId is the actual assessment ID inside the resource.
              const isCurrentAssessment =
                parentId === assessmentId || parentItemId === assessmentId;

              if (taskCompleted || taskFailed) {
                // * Resetting conversation data in cache, after error or success
                await client.refetchQueries({
                  include: [
                    {
                      query: getAIConversationsQuery,
                      variables: { id: extractionConversationId, portalType },
                    },
                  ],
                });
              }

              if (taskFailed) {
                updateExtractFromDoc({
                  assessmentId,
                  data: {
                    isExtracting: false,
                    progress: 0,
                    runningProgress: 0,
                  },
                });
                if (isCurrentAssessment) {
                  updateWorksheetSidebar({ isOpen: true });
                  updateActiveQuestion(null);
                }
                return;
              }

              updateExtractFromDoc({
                assessmentId,
                data: {
                  progress: percentage,
                  runningProgress: percentage,
                },
              });

              if (taskCompleted || taskFailed) {
                // To remove extract from doc metadata from ai conversations
                updateExtractFromDoc({
                  assessmentId,
                  data: {
                    isExtracting: false,
                    progress: 0,
                    runningProgress: 0,
                  },
                });

                if (taskCompleted && isCurrentAssessment) {
                  const refetchQueries = [];
                  if (_.isFunction(getAssessmentQuestions?.refetch)) {
                    refetchQueries.push(getAssessmentQuestions.refetch());
                  }
                  refetchQueries.push(
                    client.refetchQueries({
                      include: [
                        {
                          query: getAssessmentQuery,
                          variables: {
                            id: assessmentId,
                            portalType,
                            collaboratorsFilter,
                          },
                        },
                      ],
                    })
                  );
                  await Promise.allSettled(refetchQueries);

                  // Show toast in library portal
                  if (isToddleLibraryPortal) {
                    const assessmentTitle = _.get(params, "assessmentTitle");
                    setToastMsg?.({
                      title: assessmentTitle,
                      type: "success",
                      isClosable: true,
                      msg: "toastMsgs:question_extraction_completed",
                    });
                  }
                }

                if (isCurrentAssessment) {
                  updateWorksheetSidebar({ isOpen: true });
                  updateActiveQuestion(null);
                }
              }
              break;
            }
            default:
              return;
          }
        },
      };
    },
  });
};

export const getStudentAssignmentQuestionSubmissionsEnhancer = () => {
  return graphql(getStudentAssignmentQuestionSubmissionsQuery, {
    name: "getStudentAssignmentQuestionSubmissions",
    skip: ({ submissionId }) => _.isNil(submissionId),
    options: ({ assessmentSubmissionQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentSubmissionQueryVariables,
      context: { useHttpLink: true },
    }),
    props({
      getStudentAssignmentQuestionSubmissions,
      ownProps: {
        isData,
        isLoading,
        throttledCreateWorksheetSubmissionWrapper,
        submissionId,
        parentId,
        assessmentQuestionsQueryVariables,
        userType,
        studentAssignmentDetailQueryVariables,
        setToastMsg,
      },
    }) {
      const {
        variables,
        networkStatus,
      } = getStudentAssignmentQuestionSubmissions;

      const cacheData = getStudentAssignmentQuestionSubmissionsFromCache(
        variables
      );
      const submissionData = _.get(cacheData, "questionSubmissions.edges", []);
      const totalQuestionAttempted = _.get(
        cacheData,
        "questionSubmissions.totalQuestionAttempted",
        0
      );
      const submissionArray = getWorksheetSubmissions(submissionData);

      return {
        submissions: submissionArray,
        totalQuestionAttempted,
        isData: !_.isEmpty(cacheData) && isData,
        onPusherEventTriggered: async () => {
          if (userType == "student") {
            if (_.isEmpty(parentId) || _.isEmpty(submissionId)) {
              setToastMsg?.("toastMsgs:something_went_wrong");
              Sentry.captureMessage("Empty assignment or submission ID", {
                level: "error",
                extra: {
                  parentId,
                  submissionId,
                },
              });
              return;
            }
            throttledCreateWorksheetSubmissionWrapper({
              assessmentId: parentId,
              parentId: submissionId,
              refetchQueries: [
                {
                  query: isWorksheetQueriesV2Enabled()
                    ? getStudentQuestionDetailsQueryV2
                    : getStudentQuestionDetailsQuery,
                  variables: assessmentQuestionsQueryVariables,
                },
                {
                  query: getStudentAssignmentQuestionSubmissionsQuery,
                  variables: variables,
                },
                {
                  query: getIsStudentAssignmentRevampEnabled()
                    ? getStudentAssignmentQueryV2
                    : getStudentAssignmentDetailsQueryV2,
                  variables: studentAssignmentDetailQueryVariables,
                },
              ],
            });
          }
          if (userType == "staff") {
            refetchGetStudentAssignmentDetails({
              assessmentQuestionsQueryVariables,
              getStudentAssignmentQuestionSubmissionsQueryVariables: variables,
            });
          }
        },
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        refetchStudentAssignmentQuestionSubmissions:
          getStudentAssignmentQuestionSubmissions.refetch,
      };
    },
  });
};

export const getQuestionTopicsEnhancer = () => {
  return graphql(getQuestionTopicsQuery, {
    name: "getQuestionTopics",
    skip: ({ libraryType = "" }) => _.isEqual(libraryType, TODDLE_LIBRARY),
    options: ({ questionTopicVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: questionTopicVariables,
    }),
    props({
      getQuestionTopics,
      ownProps: { isData, isLoading, questionTopicVariables },
    }) {
      const { networkStatus } = getQuestionTopics;
      const cacheData = getQuestionTopicsFromCache(questionTopicVariables);

      const topics = _.get(cacheData, "content.getQuestionTags", []);

      return {
        topics,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });
};

export const getOrganizationSubjectsEnhancer = () => {
  return graphql(getOrganizationSubjectsQuery, {
    name: "getOrganizationSubjects",
    options: ({ getOrganizationSubjectsVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: getOrganizationSubjectsVariables,
    }),
    props: ({
      getOrganizationSubjects,
      ownProps: { getOrganizationSubjectsVariables, isData, isLoading },
    }) => {
      const cacheData = getOrganizationSubjectsFromCache(
        getOrganizationSubjectsVariables
      );

      const organizationSubjects = _.get(cacheData, "subjects", []);

      const subjectOptions = _.map(organizationSubjects, subject => {
        return {
          label: _.get(subject, "name", ""),
          value: _.get(subject, "id"),
          subjectGroup: _.get(subject, "subjectGroup"),
        };
      });

      return {
        subjectOptions,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationSubjects["networkStatus"]) ||
          isLoading,
      };
    },
  });
};

export const getOrganizationGradeBasicDetailsEnhancer = () => {
  return graphql(getOrganizationGradeBasicDetailsQuery, {
    name: "getOrganizationGrades",
    options: ({ getOrganizationGradesVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: getOrganizationGradesVariables,
    }),
    props({
      getOrganizationGrades,
      ownProps: { getOrganizationGradesVariables, isData, isLoading },
    }) {
      const cacheData = getOrganizationBasicGradeDetailsFromCache(
        getOrganizationGradesVariables
      );

      const organizationGrades = _.get(cacheData, "grades", []);

      const gradeOptions = _.map(organizationGrades, grade => {
        return {
          label: _.get(grade, "name", ""),
          value: _.get(grade, "id"),
        };
      });

      return {
        isData: !_.isEmpty(cacheData) && isData,
        gradeOptions,
        isLoading:
          _.includes([1, 2], getOrganizationGrades["networkStatus"]) ||
          isLoading,
      };
    },
  });
};

export const getQuestionPaperTypesEnhancer = () => {
  return graphql(getQuestionTopicsQuery, {
    name: "getQuestionTopics",
    skip: ({ libraryType = "" }) => _.isEqual(libraryType, SCHOOL_LIBRARY),
    options: ({ questionPaperTypesVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: questionPaperTypesVariables,
    }),
    props({
      getQuestionTopics,
      ownProps: { isData, isLoading, questionPaperTypesVariables },
    }) {
      const { networkStatus } = getQuestionTopics;
      const cacheData = getQuestionTopicsFromCache(questionPaperTypesVariables);

      const paperTypes = _.get(cacheData, "content.getQuestionTags", []);

      return {
        paperTypes,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });
};

/**
 * @deprecated use getStudentQuestionDetailsEnhancerV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getStudentQuestionDetailsEnhancer = () => {
  return graphql(getStudentQuestionDetailsQuery, {
    name: "getStudentQuestionDetails",
    skip: ({ assessmentId, parentType, userType }) =>
      _.isNil(assessmentId) ||
      _.isNil(parentType) ||
      !_.isEqual(parentType, "ASSESSMENT") ||
      isWorksheetQueriesV2Enabled() ||
      _.isEqual(userType, "staff"),
    options: ({ assessmentQuestionsQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentQuestionsQueryVariables,
    }),
    props({ getStudentQuestionDetails, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getStudentQuestionDetails;

      const cacheData = _.get(getStudentQuestionDetails, "node", {});
      const data = getWorksheetSectionsData(cacheData);

      return {
        data,
        title: _.get(cacheData, "title.value", ""),
        isDeleted: _.get(cacheData, "isDeleted", false),
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        worksheetInstructions: _.get(cacheData, "description.value", ""),
      };
    },
  });
};

export const getStudentAssignmentDetailsEnhancer = () => {
  return graphql(getStudentAssignmentDetailsQueryV2, {
    skip: ({
      studentAssignmentId,
      userType,
      isStudentAssignmentRevampEnabled,
    }) =>
      _.isEqual(userType, "staff") ||
      _.isEmpty(studentAssignmentId) ||
      isStudentAssignmentRevampEnabled,
    name: "getStudentAssignmentDetailsV2",
    options: ({ studentAssignmentDetailQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: studentAssignmentDetailQueryVariables,
    }),
    props({ getStudentAssignmentDetailsV2, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getStudentAssignmentDetailsV2;
      const studentAssignmentData = _.get(
        getStudentAssignmentDetailsV2,
        "node",
        {}
      );
      return {
        isData: !_.isEmpty(studentAssignmentData) && isData,
        studentAssignmentData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        refetchStudentAssignmentDetails: getStudentAssignmentDetailsV2.refetch,
      };
    },
  });
};

export const getStudentAssignmentEnhancer = () => {
  return graphql(getStudentAssignmentQueryV2, {
    skip: ({
      studentAssignmentId,
      userType,
      isStudentAssignmentRevampEnabled,
    }) =>
      _.isEqual(userType, "staff") ||
      _.isEmpty(studentAssignmentId) ||
      !isStudentAssignmentRevampEnabled,
    name: "getStudentAssignmentV2",
    options: ({ studentAssignmentDetailQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: studentAssignmentDetailQueryVariables,
    }),
    props({ getStudentAssignmentV2, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getStudentAssignmentV2;
      const studentAssignmentData = _.get(getStudentAssignmentV2, "node", {});
      return {
        isData: !_.isEmpty(studentAssignmentData) && isData,
        studentAssignmentData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        refetchStudentAssignmentDetails: getStudentAssignmentV2.refetch,
      };
    },
  });
};

// This enhancer is used to fetch questions details at teacher end for the new V2 version of the worksheet queries.
export const getAssessmentQuestionsEnhancerV2 = () => {
  return graphql(getAssessmentQuestionsQueryV2, {
    name: "getAssessmentQuestionsV2",
    skip: ({
      parentId,
      parentType,
      isFromToddleLibrary,
      userType,
      importFromToddleLibrary,
    }) =>
      _.isNil(parentId) ||
      _.isNil(parentType) ||
      !_.isEqual(parentType, "ASSESSMENT") ||
      _.includes(["student", "parent"], userType) ||
      !isWorksheetQueriesV2Enabled(importFromToddleLibrary) ||
      isFromToddleLibrary,
    options: ({ assessmentQuestionsQueryVariablesV2 }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentQuestionsQueryVariablesV2,
      context: { useHttpLink: true },
    }),
    props({
      getAssessmentQuestionsV2,
      ownProps: {
        isData,
        isLoading,
        updateExtractFromDoc,
        updateActiveQuestion,
        updateWorksheetSidebar,
        parentId,
        portalType,
        collaboratorsFilter,
        setToastMsg,
        isToddleLibraryPortal,
      },
    }) {
      const { networkStatus } = getAssessmentQuestionsV2;

      const cacheData = _.get(getAssessmentQuestionsV2, "node", {});

      const { data, parentItemId } = generateQuestionsHierarchy(
        _.get(cacheData, "questions.edges", [])
      );

      return {
        data,
        parentItemId,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        getAssessmentQuestions: getAssessmentQuestionsV2,
        onPusherEventTriggered: async params => {
          switch (params?.event) {
            case "questionExtractionJob": {
              const assessmentId = _.get(params, "assessment_id");
              const taskCompleted = _.get(params, "taskState") === "COMPLETED";
              const taskFailed = _.get(params, "taskState") === "FAILED";
              const percentage = _.get(params, "percentage", 0);
              const extractionConversationId = _.get(
                params,
                "aiConversationId"
              );
              const isCurrentAssessment =
                parentId === assessmentId || parentItemId === assessmentId;

              if (taskCompleted || taskFailed) {
                // * Resetting conversation data in cache, after error or success
                await client.refetchQueries({
                  include: [
                    {
                      query: getAIConversationsQuery,
                      variables: { id: extractionConversationId, portalType },
                    },
                  ],
                });
              }

              if (taskFailed) {
                updateExtractFromDoc({
                  assessmentId,
                  data: {
                    isExtracting: false,
                    progress: 0,
                    runningProgress: 0,
                  },
                });
                if (isCurrentAssessment) {
                  updateWorksheetSidebar({ isOpen: true });
                  updateActiveQuestion(null);
                }
                return;
              }

              updateExtractFromDoc({
                assessmentId,
                data: {
                  progress: percentage,
                  runningProgress: percentage,
                },
              });

              if (taskCompleted || taskFailed) {
                // To remove extract from doc metadata from ai conversations
                updateExtractFromDoc({
                  assessmentId,
                  data: {
                    isExtracting: false,
                    progress: 0,
                    runningProgress: 0,
                  },
                });

                if (taskCompleted && isCurrentAssessment) {
                  const refetchQueries = [];
                  if (_.isFunction(getAssessmentQuestionsV2?.refetch)) {
                    refetchQueries.push(getAssessmentQuestionsV2.refetch());
                  }
                  refetchQueries.push(
                    client.refetchQueries({
                      include: [
                        {
                          query: getAssessmentQuery,
                          variables: {
                            id: assessmentId,
                            portalType,
                            collaboratorsFilter,
                          },
                        },
                      ],
                    })
                  );
                  await Promise.allSettled(refetchQueries);

                  // Show toast in library portal
                  if (isToddleLibraryPortal) {
                    const assessmentTitle = _.get(params, "assessmentTitle");
                    setToastMsg?.({
                      title: assessmentTitle,
                      type: "success",
                      isClosable: true,
                      msg: "toastMsgs:question_extraction_completed",
                    });
                  }
                }

                if (isCurrentAssessment) {
                  updateWorksheetSidebar({ isOpen: true });
                  updateActiveQuestion(null);
                }
              }
              break;
            }
            default:
              return;
          }
        },
      };
    },
  });
};

// This enhancer is used to fetch question details at student and family end for the new V2 version of the worksheet queries.
export const getStudentQuestionDetailsEnhancerV2 = () => {
  return graphql(getStudentQuestionDetailsQueryV2, {
    name: "getStudentQuestionDetailsV2",
    skip: ({ assessmentId, parentType, userType }) =>
      _.isNil(assessmentId) ||
      _.isNil(parentType) ||
      !_.isEqual(parentType, "ASSESSMENT") ||
      !isWorksheetQueriesV2Enabled() ||
      _.isEqual(userType, "staff"),
    options: ({ assessmentQuestionsQueryVariablesV2 }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentQuestionsQueryVariablesV2,
    }),
    props({ getStudentQuestionDetailsV2, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getStudentQuestionDetailsV2;

      const cacheData = _.get(getStudentQuestionDetailsV2, "node", {});

      const { data } = generateQuestionsHierarchy(
        _.get(cacheData, "questions.edges", [])
      );

      return {
        data,
        title: _.get(cacheData, "title.value", ""),
        isDeleted: _.get(cacheData, "isDeleted", false),
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        worksheetInstructions: _.get(cacheData, "description.value", ""),
      };
    },
  });
};

/**
 * @deprecated use getIsWorksheetsDataLoadedV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
/**
 * @returns {boolean} Returns true if assessment questions data is loaded
 */
export const getIsWorksheetsDataLoaded = () => {
  return graphql(getAssessmentQuestionsQuery, {
    name: "getAssessmentQuestions",
    skip: ({
      parentId,
      assessmentQuestionsQueryVariables,
      isWorksheetQuestionsStep,
      isWorksheetCreationStep,
    }) =>
      !parentId ||
      _.isEmpty(assessmentQuestionsQueryVariables) ||
      (!isWorksheetQuestionsStep && !isWorksheetCreationStep) ||
      isWorksheetQueriesV2Enabled(),
    options: ({ assessmentQuestionsQueryVariables }) => ({
      fetchPolicy: "cache-only",
      variables: assessmentQuestionsQueryVariables,
    }),
    props: ({ getAssessmentQuestions, ownProps: { mode, t } }) => {
      const cacheData = _.get(getAssessmentQuestions, "node", {});

      const data = getWorksheetSectionsData(cacheData);

      const { questionCount, totalMarks } = getSideBarOptions({
        data,
        mode,
        t,
      });

      return {
        isWorksheetDataLoaded: !_.isEmpty(cacheData),
        questionCount,
        totalMarks,
      };
    },
  });
};

/**
 *
 *  @returns {boolean} Returns true if assessment questions data is loaded
 */
export const getIsWorksheetsDataLoadedV2 = () => {
  return graphql(getAssessmentQuestionsQueryV2, {
    name: "getAssessmentQuestionsV2",
    skip: ({
      parentId,
      assessmentQuestionsQueryVariablesV2,
      isWorksheetQuestionsStep,
      isWorksheetCreationStep,
    }) =>
      !parentId ||
      _.isEmpty(assessmentQuestionsQueryVariablesV2) ||
      (!isWorksheetQuestionsStep && !isWorksheetCreationStep) ||
      !isWorksheetQueriesV2Enabled(),
    options: ({ assessmentQuestionsQueryVariablesV2 }) => ({
      fetchPolicy: "cache-only",
      variables: assessmentQuestionsQueryVariablesV2,
    }),
    props: ({ getAssessmentQuestionsV2, ownProps: { mode, t } }) => {
      const cacheData = _.get(getAssessmentQuestionsV2, "node", {});

      const { data } = generateQuestionsHierarchy(
        _.get(cacheData, "questions.edges", [])
      );

      const { questionCount, totalMarks } = getSideBarOptionsV2({
        data,
        mode,
        t,
      });

      return {
        isWorksheetDataLoaded: !_.isEmpty(cacheData),
        questionCount,
        totalMarks,
      };
    },
  });
};

// Enhancer for worksheet download job count
export const getWorksheetDownloadJobCountEnhancer = () => {
  return graphql(getWorksheetDownloadJobCountQuery, {
    name: "getWorksheetDownloadJobCount",
    skip: ({ organizationId, isWorksheet }) =>
      _.isEmpty(organizationId) || !isWorksheet,
    options: ({
      organizationId,
      assignmentId,
      worksheetSubmissionJobCountFilter,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: {
          ...worksheetSubmissionJobCountFilter,
          relatedEntityId: assignmentId,
        },
      },
    }),
    props: ({
      getWorksheetDownloadJobCount,
      ownProps: { isData, isLoading },
    }) => {
      const worksheetQueuedJobCount = _.get(
        getWorksheetDownloadJobCount,
        "node.jobFeed.totalCount",
        0
      );

      return {
        getWorksheetDownloadJobCount,
        worksheetQueuedJobCount,
        isData: !_.isEmpty(getWorksheetDownloadJobCount) && isData,
        isLoading:
          _.includes([1, 2], getWorksheetDownloadJobCount["networkStatus"]) ||
          isLoading,
        onPusherEventTriggered: params => {
          if (params?.event === "bulkDownloadForStudentResponseIsReady") {
            getWorksheetDownloadJobCount.refetch();
          }
        },
      };
    },
  });
};

export const getToddleQuestionLibraryFeedV2Enhancer = () =>
  graphql(getToddleQuestionLibraryFeedQueryV2, {
    name: "getToddleQuestionLibraryFeedV2",
    skip: ({
      libraryType = "",
      questionLibraryFeedVariables,
      isQueriesV2Enabled,
    }) =>
      !_.isEqual(libraryType, TODDLE_LIBRARY) ||
      !isQueriesV2Enabled ||
      _.isEmpty(_.get(questionLibraryFeedVariables, "filters.grades", [])),
    options: ({ questionLibraryFeedVariables }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: questionLibraryFeedVariables,
    }),
    props({
      getToddleQuestionLibraryFeedV2,
      ownProps: { questionLibraryFeedVariables, isLoading, isData },
    }) {
      const { networkStatus } = getToddleQuestionLibraryFeedV2;
      const cacheData = _.get(getToddleQuestionLibraryFeedV2, "node", {});
      const libraryEntities = _.get(cacheData, "libraryEntities", {});
      const totalCount = _.get(libraryEntities, "totalCount", 0);
      const libraryData = _.get(libraryEntities, "edges", []);
      const hasNextPage = _.get(libraryEntities, "pageInfo.hasNextPage", false);
      const endCursor = _.get(libraryEntities, "pageInfo.endCursor", "");

      return {
        libraryData,
        totalCount,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: { ...questionLibraryFeedVariables, after: endCursor },
          queryData: getToddleQuestionLibraryFeedV2,
          key: "libraryEntities",
        }),
      };
    },
  });
